"use client";

import ScrollReveal from "@/components/common/ScrollReveal";
import PageTitle from "@/components/common/PageTitle";

const Privacy = () => {
  return (
    <div className="flex flex-col gap-8 sm:gap-10 lg:gap-12">
      {/* The padding tops the parent's gap up to the 56px every tabbed subpage
          leaves under its header block (48px layout + pt-2). Here the rule under
          the title plays the part the tab row plays there, so the first
          paragraph sits the same distance below it: 40+16 from md, 48+8 from lg.
          Left alone below md, where the site-wide figure is 32px. */}
      <div className="flex flex-col gap-4 md:pb-4 lg:pb-2">
        {/* Page title in the site's product-page style: 5px red accent bar at the
            content margin, title text nudged past it.

            Deliberately outside the ScrollReveal below: PageTitle brings its own
            entrance (the scan reveal every page title on the site now uses), and
            nesting it in a fade-up would run two entrances over the one heading. */}
        <PageTitle>ECQ Privacy Policy</PageTitle>

        <ScrollReveal direction="up" delay={0}>
          <div className="h-[1px] w-full border-[1px] border-neutral-500"></div>
        </ScrollReveal>
      </div>

      <ScrollReveal direction="up" delay={100}>
        <p className="body-1 text-text-highlight">
          E-CQURITY is committed to protecting the privacy and security of your personal
          information. This Privacy Policy outlines how we collect, use, disclose, and safeguard
          your information when you use our services or interact with our website. By accessing or
          using our services, you acknowledge that you have read and understood this Privacy Policy.
        </p>
      </ScrollReveal>

      <ScrollReveal direction="up" delay={200}>
        <section className="flex flex-col gap-2">
          <h2 className="font-bold text-[24px] sm:text-[28px] lg:text-[30px]">
            Information We Collect
          </h2>
          <div>
            <p className="body-1">
              We may collect personal information directly from you or automatically when you use
              our services. The types of information we may collect include:
            </p>
            <ol className="body-1 space-y-2">
              <li>
                <strong className="body-1-heavy text-primary-600">1.1. </strong> Personal
                Information – Name, Contact information (email address, phone number, mailing
                address), Company name (if applicable), Job title (if applicable).
              </li>
              <li>
                <strong className="body-1-heavy text-primary-600">1.2. </strong> Log and Usage Data
                – IP addresses, Browser type, Operating system, Referring/exit pages, Date and time
                stamps, Clickstream data, Other usage information.
              </li>
            </ol>
          </div>
        </section>
      </ScrollReveal>

      <ScrollReveal direction="up" delay={300}>
        <section className="flex flex-col gap-2">
          <h2 className="font-bold text-[24px] sm:text-[28px] lg:text-[30px]">
            How We Use Your Information?
          </h2>
          <div>
            <p className="body-1">
              We may use the information we collect for various purposes, including but not limited
              to:
            </p>
            <ol className="body-1 space-y-2">
              <li>
                <strong className="body-1-heavy text-primary-600">2.1. </strong> Providing and
                improving our services
              </li>
              <li>
                <strong className="body-1-heavy text-primary-600">2.2. </strong> Responding to your
                inquiries and customer support requests
              </li>

              <li>
                <strong className="body-1-heavy text-primary-600">2.3. </strong> Sending you
                updates, newsletters, and promotional materials
              </li>
              <li>
                <strong className="body-1-heavy text-primary-600">2.4. </strong> Conducting research
                and analysis to enhance our products and services
              </li>
              <li>
                <strong className="body-1-heavy text-primary-600">2.5. </strong> Customizing your
                experience and preferences
              </li>
              <li>
                <strong className="body-1-heavy text-primary-600">2.6. </strong> Detecting and
                preventing fraud, security breaches, or other potentially prohibited or illegal
                activities
              </li>
            </ol>
          </div>
        </section>
      </ScrollReveal>

      <ScrollReveal direction="up" delay={400}>
        <section className="flex flex-col gap-2">
          <h2 className="font-bold text-[24px] sm:text-[28px] lg:text-[30px]">
            How We Share Your Information?
          </h2>
          <div className="flex flex-col gap-2">
            <p className="body-1">
              We may share your personal information with third parties in the following
              circumstances:
            </p>
            <ol className="flex flex-col gap-4 body-1">
              <li>
                <strong className="body-1-heavy text-primary-600">3.1. </strong> Service Providers:
                <p className="mt-2">
                  We may disclose your information to third-party service providers who assist us in
                  performing various tasks, such as hosting our website, analyzing data, providing
                  customer support, or conducting surveys. These service providers are obligated to
                  keep your information confidential and secure.
                </p>
              </li>
              <li>
                <strong className="body-1-heavy text-primary-600">3.2. </strong> Legal Requirements:
                <p className="mt-2">
                  We may disclose your information if required to do so by law or in response to
                  valid legal requests, such as subpoenas, court orders, or government regulations.
                </p>
              </li>
              <li>
                <strong className="body-1-heavy text-primary-600">3.3. </strong> Business Transfers:
                <p className="mt-2">
                  If E-CQURITY PTE. LTD. undergoes a merger, acquisition, or sale of all or a
                  portion of its assets, your information may be transferred as part of the
                  transaction. In such cases, we will provide notice before your information is
                  transferred and becomes subject to a different privacy policy.
                </p>
              </li>
            </ol>
          </div>
        </section>
      </ScrollReveal>

      <ScrollReveal direction="up" delay={0}>
        <section className="flex flex-col gap-2">
          <h2 className="font-bold text-[24px] sm:text-[28px] lg:text-[30px]">
            Security of Your Information
          </h2>
          <p className="body-1 text-text-highlight">
            We implement reasonable security measures to protect your personal information from
            unauthorized access, alteration, disclosure, or destruction. We follow industry-standard
            practices and strive to maintain the confidentiality and integrity of your information.
            However, please note that no method of transmission over the internet or electronic
            storage is completely secure, and we cannot guarantee the absolute security of your
            information.
          </p>
        </section>
      </ScrollReveal>

      <ScrollReveal direction="up" delay={100}>
        <section className="flex flex-col gap-2">
          <h2 className="font-bold text-[24px] sm:text-[28px] lg:text-[30px]">
            Third-Party Links and Services{" "}
          </h2>
          <p className="body-1 text-text-highlight">
            Our services may contain links to third-party websites or services. We are not
            responsible for the privacy practices or content of these third parties. We encourage
            you to read their privacy policies and terms of service before interacting with them.
          </p>
        </section>
      </ScrollReveal>

      <ScrollReveal direction="up" delay={200}>
        <section className="flex flex-col gap-2">
          <h2 className="font-bold text-[24px] sm:text-[28px] lg:text-[30px]">
            International Data Transfers
          </h2>
          <p className="body-1 text-text-highlight">
            If you access our services from outside Singapore, your information may be transferred
            to and stored on servers located in Singapore or other countries. By providing your
            information and using our services, you consent to such transfer and storage of your
            information.
          </p>
        </section>
      </ScrollReveal>

      <ScrollReveal direction="up" delay={300}>
        <section className="flex flex-col gap-2">
          <h2 className="font-bold text-[24px] sm:text-[28px] lg:text-[30px]">
            Retention of Your Information
          </h2>
          <p className="body-1 text-text-highlight">
            We will retain your personal information for as long as necessary to fulfill the
            purposes outlined in this Privacy Policy. We will also retain and use your information
            as necessary to comply with our legal obligations, resolve disputes, and enforce our
            agreements.
          </p>
        </section>
      </ScrollReveal>

      <ScrollReveal direction="up" delay={400}>
        <section className="flex flex-col gap-2">
          <h2 className="font-bold text-[24px] sm:text-[28px] lg:text-[30px]">
            Your Rights and Choices
          </h2>
          <p className="body-1 text-text-highlight">
            You may have certain rights and choices regarding the personal information we collect
            about you. These rights may include accessing, correcting, or deleting your information.
            If you would like to exercise any of these rights or make changes to your information,
            please contact us with the details below.
          </p>
        </section>
      </ScrollReveal>

      <ScrollReveal direction="up" delay={0}>
        <section className="flex flex-col gap-2">
          <h2 className="font-bold text-[24px] sm:text-[28px] lg:text-[30px]">
            Updates to this Privacy Policy
          </h2>
          <p className="body-1 text-text-highlight">
            We may update this Privacy Policy from time to time to reflect changes in our privacy
            practices. We will notify you of any material changes by posting the updated Privacy
            Policy on our website or through other appropriate communication channels. We encourage
            you to review this Privacy Policy periodically for any updates.
          </p>
        </section>
      </ScrollReveal>

      <ScrollReveal direction="up" delay={100}>
        <section className="flex flex-col gap-2">
          <h2 className="font-bold text-[24px] sm:text-[28px] lg:text-[30px]">Contact Us</h2>
          <p className="body-1 text-text-highlight">
            If you have any questions, concerns, or requests regarding this Privacy Policy or the
            handling of your personal information, please contact us at:
          </p>

          <div className="flex flex-col gap-4 mt-4">
            <div className="flex flex-row items-center gap-4 sm:gap-9">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-neutral-900">
                <svg
                  width="12"
                  height="10"
                  viewBox="0 0 12 10"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g id="email copy">
                    <path
                      id="Shape"
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M10.8 0.195312H1.2C0.54 0.195312 0 0.735313 0 1.39531V8.59531C0 9.25531 0.54 9.79531 1.2 9.79531H10.8C11.46 9.79531 12 9.25531 12 8.59531V1.39531C12 0.735313 11.46 0.195312 10.8 0.195312ZM10.8001 2.59648L6.00007 5.59648L1.20007 2.59648V1.39648L6.00007 4.39648L10.8001 1.39648V2.59648Z"
                      fill="#EC1938"
                    />
                  </g>
                </svg>
              </div>
              <a
                href="mailto:DPO@e-cq.net"
                className="body-1 break-all hover:text-primary transition-colors duration-300"
              >
                DPO@e-cq.net
              </a>
            </div>

            <div className="flex flex-row items-start gap-4 sm:gap-9">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-neutral-900">
                <svg
                  width="13"
                  height="15"
                  viewBox="0 0 13 15"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M2.15527 2.17578C4.49754 -0.194009 8.29934 -0.194008 10.6416 2.17578C12.9839 4.54573 12.9839 8.39275 10.6416 10.7627L6.87402 14.5928C6.73825 14.7301 6.56815 14.7988 6.39844 14.7988C6.22872 14.7988 6.05863 14.7301 5.92285 14.5928L2.15527 10.7803C-0.187018 8.41033 -0.187018 4.54573 2.15527 2.17578ZM6.39844 3.37695C4.82747 3.37695 3.55096 4.66834 3.55078 6.25781C3.55078 7.84744 4.82736 9.13965 6.39844 9.13965C7.96948 9.13961 9.24609 7.84742 9.24609 6.25781C9.24591 4.66837 7.96937 3.37699 6.39844 3.37695Z"
                    fill="#EC1938"
                  />
                </svg>
              </div>
              <div className="body-1 flex flex-col">
                <span>8 Ubi Rd 2, Zervex</span>
                <span> #06-07,</span>
                <span>Singapore 408538</span>
              </div>
            </div>
          </div>
        </section>
      </ScrollReveal>

      <ScrollReveal direction="up" delay={200}>
        <section className="flex flex-col gap-2">
          <h2 className="font-bold text-[24px] sm:text-[28px] lg:text-[30px]">Consent</h2>
          <p className="body-1 text-text-highlight">
            By using our services or providing us with your personal information, you consent to the
            collection, use, disclosure, and processing of your information as described in this
            Privacy Policy.
          </p>

          <em className="body-1 text-text-highlight mt-4">
            *Please note that this Privacy Policy is intended to provide you with general
            information about our privacy practices and does not create any contractual or legal
            rights.*
          </em>
        </section>
      </ScrollReveal>
    </div>
  );
};

export default Privacy;
